// =====================================================
// CompareDrawer — Full-screen side-by-side comparison
// Opens as a Sheet overlay. Shows a structured table
// comparing key attributes across 2-4 scholarships.
// =====================================================

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  DollarSign,
  Calendar,
  Users,
  Trophy,
  FileText,
  Check,
  Minus,
  ExternalLink,
  Clock,
  GraduationCap,
  ArrowRightLeft,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useCompare } from '@/contexts/CompareContext';
import { useProfile } from '@/contexts/ProfileContext';
import { getScholarshipMatch } from '@/services/matchCalculator';
import MatchBadge from '@/components/ui/MatchBadge';
import {
  scholarshipRequiresEssay,
  scholarshipRequiresRecLetter,
} from '@/services/essayTemplateService';
import { getDeadlineUrgency } from '@/utils/deadlineUrgency';
import { cn } from '@/lib/utils';
import type { Scholarship } from '@/types';

// ── Comparison row data ──────────────────────────────

interface CompareRow {
  label: string;
  icon: React.ReactNode;
  values: React.ReactNode[];
  /** Optional: highlight the "best" column index (0-based) */
  bestIndex?: number;
}

/** Parse dollar amount string to numeric for comparison */
function parseAmount(amt: string): number {
  if (amt.toLowerCase().includes('full ride') || amt.toLowerCase().includes('full tuition')) return 100000;
  if (amt.toLowerCase().includes('varies')) return 5000;
  const match = amt.replace(/,/g, '').match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

const CompareDrawer = () => {
  const { compareList, removeFromCompare, isDrawerOpen, closeDrawer } = useCompare();
  const { profile, isProfileComplete } = useProfile();

  // Calculate matches for each scholarship
  const matchResults = useMemo(() => {
    if (!isProfileComplete) return compareList.map(() => null);
    return compareList.map(s => getScholarshipMatch(profile, s));
  }, [compareList, profile, isProfileComplete]);

  // Build comparison rows
  const rows: CompareRow[] = useMemo(() => {
    if (compareList.length < 2) return [];

    const amounts = compareList.map(s => parseAmount(s.amount));
    const maxAmountIdx = amounts.indexOf(Math.max(...amounts));

    const deadlines = compareList.map(s => s.daysLeft);
    const maxDeadlineIdx = deadlines.indexOf(Math.max(...deadlines));

    const matches = matchResults.map(r => r?.overallMatch ?? -1);
    const bestMatchIdx = matches.every(m => m === -1) ? undefined : matches.indexOf(Math.max(...matches));

    return [
      // Match Score
      {
        label: 'Match Score',
        icon: <GraduationCap className="w-4 h-4" />,
        values: matchResults.map((r, i) =>
          r ? (
            <MatchBadge key={i} match={r.overallMatch} size="md" />
          ) : (
            <span className="text-xs text-ds-gray-3 italic">Complete profile</span>
          )
        ),
        bestIndex: bestMatchIdx,
      },
      // Award Amount
      {
        label: 'Award',
        icon: <DollarSign className="w-4 h-4 text-ds-gold" />,
        values: compareList.map((s, i) => (
          <div key={i}>
            <span className="text-lg font-bold text-ds-gold">{s.amount}</span>
            {s.amountDetail && (
              <p className="text-[10px] text-ds-gray-3 mt-0.5">{s.amountDetail}</p>
            )}
          </div>
        )),
        bestIndex: maxAmountIdx,
      },
      // Deadline
      {
        label: 'Deadline',
        icon: <Calendar className="w-4 h-4" />,
        values: compareList.map((s, i) => {
          const u = getDeadlineUrgency(s.daysLeft);
          return (
            <div key={i}>
              <span className="text-sm font-medium text-ds-gray-1">{s.deadline}</span>
              <div className={cn("inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium", u.bg, u.text)}>
                <Clock className="w-3 h-3" />
                {s.daysLeft}d
              </div>
            </div>
          );
        }),
        bestIndex: maxDeadlineIdx,
      },
      // Category
      {
        label: 'Category',
        icon: <Trophy className="w-4 h-4" />,
        values: compareList.map((s, i) => (
          <span key={i} className="text-sm text-ds-gray-1">{s.category}</span>
        )),
      },
      // Organization
      {
        label: 'Organization',
        icon: <Users className="w-4 h-4" />,
        values: compareList.map((s, i) => (
          <span key={i} className="text-sm text-ds-gray-2">{s.org}</span>
        )),
      },
      // Applicants
      {
        label: 'Applicants',
        icon: <Users className="w-4 h-4" />,
        values: compareList.map((s, i) => (
          <span key={i} className="text-sm text-ds-gray-1">{s.applicants}</span>
        )),
      },
      // Awards Given
      {
        label: 'Awards/Year',
        icon: <Trophy className="w-4 h-4" />,
        values: compareList.map((s, i) => (
          <span key={i} className="text-sm text-ds-gray-1">{s.awardsGiven}</span>
        )),
      },
      // Essay Required
      {
        label: 'Essay Required',
        icon: <FileText className="w-4 h-4" />,
        values: compareList.map((s, i) => {
          const requires = scholarshipRequiresEssay(s);
          return (
            <span key={i} className={cn("text-sm font-medium", requires ? 'text-ds-gray-1' : 'text-ds-green')}>
              {requires ? 'Yes' : 'No'}
              {!requires && <Check className="w-3.5 h-3.5 inline ml-1" />}
            </span>
          );
        }),
      },
      // Rec Letter Required
      {
        label: 'Rec Letters',
        icon: <FileText className="w-4 h-4" />,
        values: compareList.map((s, i) => {
          const requires = scholarshipRequiresRecLetter(s);
          return (
            <span key={i} className={cn("text-sm font-medium", requires ? 'text-ds-gray-1' : 'text-ds-green')}>
              {requires ? 'Yes' : 'No'}
              {!requires && <Check className="w-3.5 h-3.5 inline ml-1" />}
            </span>
          );
        }),
      },
      // Total Requirements
      {
        label: 'Requirements',
        icon: <FileText className="w-4 h-4" />,
        values: compareList.map((s, i) => (
          <span key={i} className="text-sm text-ds-gray-1">
            {s.requirements.length} item{s.requirements.length !== 1 ? 's' : ''}
          </span>
        )),
      },
      // School Spotlight
      {
        label: 'School Winners',
        icon: <GraduationCap className="w-4 h-4" />,
        values: compareList.map((s, i) =>
          s.schoolWinners > 0 ? (
            <span key={i} className="text-sm text-ds-green font-medium">{s.schoolWinners} winners</span>
          ) : (
            <Minus key={i} className="w-4 h-4 text-ds-gray-4" />
          )
        ),
      },
    ];
  }, [compareList, matchResults]);

  // ── Eligibility criteria comparison ─────────────────
  const eligibilityCriteria = useMemo(() => {
    if (!isProfileComplete || matchResults.every(r => r === null)) return [];

    // Gather all unique criteria labels across all compared scholarships
    const allLabels = new Set<string>();
    matchResults.forEach(r => {
      r?.criteriaResults.forEach(c => allLabels.add(c.label));
    });

    return Array.from(allLabels).map(label => ({
      label,
      results: matchResults.map(r => {
        const c = r?.criteriaResults.find(cr => cr.label === label);
        if (!c) return 'na' as const;
        return c.matches ? ('pass' as const) : ('fail' as const);
      }),
    }));
  }, [matchResults, isProfileComplete]);

  return (
    <Sheet open={isDrawerOpen} onOpenChange={closeDrawer}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[90vw] lg:max-w-[80vw] bg-ds-bg border-ds-border p-0 overflow-y-auto"
      >
        {/* Header */}
        <SheetHeader className="sticky top-0 z-10 bg-ds-bg/95 backdrop-blur-lg border-b border-ds-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-ds-red/20 flex items-center justify-center">
                <ArrowRightLeft className="w-5 h-5 text-ds-red" />
              </div>
              <div>
                <SheetTitle className="text-ds-gray-1 text-xl font-display">
                  Compare Scholarships
                </SheetTitle>
                <p className="text-xs text-ds-gray-3 mt-0.5">
                  {compareList.length} scholarships selected
                </p>
              </div>
            </div>
          </div>
        </SheetHeader>

        <div className="px-4 sm:px-6 py-6 overflow-x-auto">
          {compareList.length < 2 ? (
            <div className="text-center py-16">
              <ArrowRightLeft className="w-12 h-12 text-ds-gray-4 mx-auto mb-4" />
              <p className="text-ds-gray-2 text-lg font-medium">Select at least 2 scholarships to compare</p>
              <p className="text-ds-gray-3 text-sm mt-1">Use the compare button on scholarship cards</p>
            </div>
          ) : (
            <>
              {/* ── Scholarship Headers ── */}
              <div className="grid gap-4 mb-6 min-w-[600px]" style={{ gridTemplateColumns: `160px repeat(${compareList.length}, 1fr)` }}>
                {/* Empty top-left cell */}
                <div />

                {compareList.map(s => (
                  <div key={s.id} className="text-center">
                    {/* Gradient bar */}
                    <div className="h-2 rounded-full mb-3 mx-4" style={{ background: s.heroGradient }} />
                    <h3 className="text-sm font-semibold text-ds-gray-1 line-clamp-2 mb-1">
                      {s.name}
                    </h3>
                    <p className="text-[10px] text-ds-gray-3 truncate mb-2">{s.org}</p>
                    <div className="flex items-center justify-center gap-2">
                      <Link
                        to={`/scholarship/${s.id}`}
                        onClick={closeDrawer}
                        className="text-[10px] text-ds-red hover:underline flex items-center gap-0.5"
                      >
                        View <ExternalLink className="w-2.5 h-2.5" />
                      </Link>
                      <button
                        onClick={() => removeFromCompare(s.id)}
                        className="text-[10px] text-ds-gray-3 hover:text-ds-red flex items-center gap-0.5 transition-colors"
                      >
                        <X className="w-2.5 h-2.5" />
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Comparison Table ── */}
              <div className="rounded-xl border border-ds-border overflow-hidden min-w-[600px]">
                {rows.map((row, rowIdx) => (
                  <div
                    key={row.label}
                    className={cn(
                      "grid items-center gap-4 px-4 py-3",
                      rowIdx % 2 === 0 ? 'bg-ds-card' : 'bg-ds-bg',
                      rowIdx < rows.length - 1 && 'border-b border-ds-border'
                    )}
                    style={{ gridTemplateColumns: `160px repeat(${compareList.length}, 1fr)` }}
                  >
                    {/* Row label */}
                    <div className="flex items-center gap-2 text-ds-gray-2">
                      {row.icon}
                      <span className="text-xs font-medium">{row.label}</span>
                    </div>

                    {/* Values */}
                    {row.values.map((val, colIdx) => (
                      <div
                        key={colIdx}
                        className={cn(
                          "text-center",
                          row.bestIndex === colIdx && 'relative'
                        )}
                      >
                        {row.bestIndex === colIdx && (
                          <div className="absolute -inset-1 rounded-lg bg-ds-green/5 border border-ds-green/20 -z-10" />
                        )}
                        {val}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* ── Eligibility Criteria Breakdown ── */}
              {eligibilityCriteria.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-sm font-semibold text-ds-gray-1 mb-3 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-ds-gold" />
                    Eligibility Breakdown (Based on Your Profile)
                  </h3>
                  <div className="rounded-xl border border-ds-border overflow-hidden">
                    {eligibilityCriteria.map((criteria, idx) => (
                      <div
                        key={criteria.label}
                        className={cn(
                          "grid items-center gap-4 px-4 py-2.5",
                          idx % 2 === 0 ? 'bg-ds-card' : 'bg-ds-bg',
                          idx < eligibilityCriteria.length - 1 && 'border-b border-ds-border'
                        )}
                        style={{ gridTemplateColumns: `160px repeat(${compareList.length}, 1fr)` }}
                      >
                        <span className="text-xs text-ds-gray-2 truncate">{criteria.label}</span>
                        {criteria.results.map((result, colIdx) => (
                          <div key={colIdx} className="text-center">
                            {result === 'pass' ? (
                              <div className="inline-flex items-center gap-1 text-ds-green">
                                <Check className="w-4 h-4" />
                                <span className="text-xs">Match</span>
                              </div>
                            ) : result === 'fail' ? (
                              <div className="inline-flex items-center gap-1 text-ds-red">
                                <X className="w-4 h-4" />
                                <span className="text-xs">No match</span>
                              </div>
                            ) : (
                              <Minus className="w-4 h-4 text-ds-gray-4 mx-auto" />
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Action Footer ── */}
              <div className="mt-8 flex flex-wrap gap-3">
                {compareList.map(s => (
                  <Link
                    key={s.id}
                    to={`/scholarship/${s.id}`}
                    onClick={closeDrawer}
                    className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 rounded-xl bg-ds-card border border-ds-border hover:border-ds-gray-3 text-ds-gray-1 text-sm font-medium transition-colors"
                  >
                    View {s.name.split(' ').slice(0, 2).join(' ')}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CompareDrawer;
